/**
 * Interface for CSS scoping result
 */
interface CssScopingResult {
  success: boolean;
  scopedCss?: string;
  error?: string;
}

/**
 * Configuration for CSS scoping
 */
interface CssScopingConfig {
  // Prefix to use for scoping (defaults to section ID)
  scopePrefix?: string;
  // Whether to wrap with ID selector (#) or class selector (.)
  useSelectorType?: 'id' | 'class';
}

/**
 * Default CSS scoping configuration
 */
const DEFAULT_CSS_CONFIG: CssScopingConfig = {
  useSelectorType: 'id',
};

/**
 * Scope CSS rules to a specific container to prevent style conflicts
 *
 * @param cssCode - The CSS code to scope
 * @param uniqueId - Unique identifier for the section
 * @param config - Optional configuration for scoping
 * @returns CssScopingResult - Result with success status and scoped CSS or error
 */
export const scopeCssToSection = (
  cssCode: string,
  uniqueId: string,
  config: CssScopingConfig = DEFAULT_CSS_CONFIG,
): CssScopingResult => {
  try {
    // Validate inputs
    if (!cssCode || typeof cssCode !== 'string') {
      return {
        success: false,
        error: 'Invalid CSS: CSS code must be a non-empty string',
      };
    }

    if (!uniqueId || typeof uniqueId !== 'string') {
      return {
        success: false,
        error: 'Invalid unique ID: ID must be a non-empty string',
      };
    }

    // Merge configuration with defaults
    const mergedConfig = { ...DEFAULT_CSS_CONFIG, ...config };

    // Create the scope selector
    const selectorPrefix = mergedConfig.useSelectorType === 'id' ? '#' : '.';
    const scopeSelector = `${selectorPrefix}${uniqueId}`;

    // Process CSS line by line to handle different rule types
    const scopedCss = processCssRules(cssCode.trim(), scopeSelector);

    return {
      success: true,
      scopedCss,
    };
  } catch (error) {
    return {
      success: false,
      error: 'Failed to scope CSS',
    };
  }
};

/**
 * Process CSS rules and apply scoping
 *
 * @param cssCode - CSS code to process
 * @param scopeSelector - Scope selector to prepend
 * @returns string - Scoped CSS
 */
const processCssRules = (cssCode: string, scopeSelector: string): string => {
  // Split CSS into logical blocks while preserving structure
  const lines = cssCode.split('\n');
  const scopedLines: string[] = [];
  let insideMediaQuery = false;
  let insideKeyframes = false;
  let braceDepth = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip empty lines and comments
    if (!line || line.startsWith('/*') || line.startsWith('//')) {
      scopedLines.push(lines[i]);
      continue;
    }

    // Track brace depth
    const openBraces = (line.match(/{/g) || []).length;
    const closeBraces = (line.match(/}/g) || []).length;
    braceDepth += openBraces - closeBraces;

    // Detect special CSS rules that shouldn't be scoped
    if (line.includes('@media')) {
      insideMediaQuery = true;
      scopedLines.push(lines[i]);
      continue;
    }

    if (line.includes('@keyframes') || line.includes('@-webkit-keyframes')) {
      insideKeyframes = true;
      scopedLines.push(lines[i]);
      continue;
    }

    // Check if we're exiting special rules
    if ((insideMediaQuery || insideKeyframes) && braceDepth === 0) {
      insideMediaQuery = false;
      insideKeyframes = false;
      scopedLines.push(lines[i]);
      continue;
    }

    // Skip scoping inside keyframes or at-rules
    if (insideKeyframes || line.startsWith('@')) {
      scopedLines.push(lines[i]);
      continue;
    }

    // Check if this line contains a CSS selector (not a property)
    if (line.includes('{') && !line.includes(':') && !insideMediaQuery) {
      // This is a CSS rule - scope it
      const scopedLine = scopeCssSelector(lines[i], scopeSelector);
      scopedLines.push(scopedLine);
    } else if (insideMediaQuery && line.includes('{') && !line.includes(':')) {
      // Inside media query - scope the selector
      const scopedLine = scopeCssSelector(lines[i], scopeSelector);
      scopedLines.push(scopedLine);
    } else {
      // This is a property line or other content
      scopedLines.push(lines[i]);
    }
  }

  return scopedLines.join('\n');
};

/**
 * Scope a single CSS selector line
 *
 * @param line - CSS line containing selector
 * @param scopeSelector - Scope selector to prepend
 * @returns string - Scoped CSS line
 */
const scopeCssSelector = (line: string, scopeSelector: string): string => {
  // Extract the selector part (before the opening brace)
  const selectorMatch = line.match(/^(\s*)([^{]+)(\s*\{.*)/);

  if (!selectorMatch) {
    return line; // Not a selector line, return as-is
  }

  const [, leadingWhitespace, selector, rest] = selectorMatch;

  // Split multiple selectors by comma
  const selectors = selector.split(',').map(s => s.trim());

  // Scope each selector
  const scopedSelectors = selectors.map(sel => {
    // Skip selectors that are already scoped or are pseudo-selectors
    if (sel.startsWith('#') && sel.includes(scopeSelector.substring(1))) {
      return sel; // Already scoped
    }

    // Handle special selectors
    if (sel.startsWith(':') || sel.startsWith('::')) {
      return `${scopeSelector}${sel}`; // Pseudo-selectors
    }

    if (sel === '*' || sel.includes('*')) {
      return `${scopeSelector} ${sel}`; // Universal selector
    }

    // Regular selector - prepend scope
    return `${scopeSelector} ${sel}`;
  });

  return `${leadingWhitespace}${scopedSelectors.join(', ')}${rest}`;
};

/**
 * Generate a unique section ID for codeblock sections
 *
 * @param sectionId - Original section ID
 * @returns string - Unique scoped ID
 */
export const generateCodeblockSectionId = (sectionId: string): string => {
  // Use deterministic ID based on section ID to avoid hydration issues
  return `codeblock-${sectionId}`;
};

/**
 * Validate CSS syntax (basic validation)
 *
 * @param cssCode - CSS code to validate
 * @returns boolean - True if CSS appears to be valid
 */
export const validateCssBasic = (cssCode: string): boolean => {
  try {
    // Basic validation: check for balanced braces
    const openBraces = (cssCode.match(/{/g) || []).length;
    const closeBraces = (cssCode.match(/}/g) || []).length;

    return openBraces === closeBraces;
  } catch {
    return false;
  }
};

// Export types for use in other modules
export type { CssScopingResult, CssScopingConfig };
